import { type ReactNode } from "react";
import { useEffect } from "react";
import { css } from "@emotion/react";
import { observer } from "mobx-react-lite";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import type { TabOptions, TabsStore } from "src/lib/tabs-ui/TabsStore.tsx";
import { TabsList } from "./TabsList.tsx";
import { TabsProvider, useTabsStore } from "./TabsProvider.tsx";
import { usePrevious } from "react-use";

export type TabsProps = {
  onPlusButtonClick?: () => void;
  plusButtonHelperText: ReactNode;
  hidePlusButton?: boolean;
  disablePlusButton?: boolean;
  store: TabsStore;
  tabs?: TabOptions[];
  pinnedLeft?: string[];
  activeTabId?: string;
  defaultActiveTabId?: string;
  hasControlledActiveTabId?: boolean;
  onActiveTabIdChange?: (id: string | undefined) => void;
  onTabsChange?: (nextTabs: TabOptions[], closedTabs: TabOptions[]) => void;
  onPinnedLeftChange?: (ids: string[]) => void;
};

export const Tabs = observer(function TabsObserved(props: TabsProps) {
  const {
    onPlusButtonClick,
    plusButtonHelperText,
    pinnedLeft,
    hidePlusButton = false,
    disablePlusButton = false,
    store,
    defaultActiveTabId,
    hasControlledActiveTabId = false,
    tabs,
    activeTabId,
    onActiveTabIdChange,
    onTabsChange,
  } = props;

  store.setHasControlledTabId(hasControlledActiveTabId);

  useEffect(() => {
    if (activeTabId != null && activeTabId !== store.activeTabId) {
      store.setActiveTabId(activeTabId, {
        emitEvents: false,
      });
    }
  }, [activeTabId, store]);

  const previousPinnedLeft = usePrevious(pinnedLeft);

  useEffect(() => {
    if (pinnedLeft != null && pinnedLeft !== previousPinnedLeft) {
      store.setPinnedLeft(pinnedLeft, {
        emitEvents: false,
      });
    }
  }, [pinnedLeft, previousPinnedLeft, store]);

  const previousTabs = usePrevious(tabs);

  useEffect(() => {
    if (tabs && previousTabs !== tabs) {
      // todo: should I preserve previous tab instance if their ids didn't change
      // or should I leave it as it is, e.g. completely replacing all tab instances when 'tabs' prop changes
      const nextTabs = tabs.map((options) => store.createTab(options));
      store.setTabs(nextTabs, {
        emitEvents: false,
      });
    }
  }, [tabs, previousTabs, store]);

  useEffect(() => {
    return store.emitter.on("tabsSet", (nextTabs, closedTabs) => {
      onTabsChange?.(nextTabs, closedTabs);
    });
  }, [onTabsChange, store, onActiveTabIdChange]);

  useEffect(() => {
    return store.emitter.on("activeTabChange", (tab) => {
      onActiveTabIdChange?.(tab?.id);
    });
  }, [onActiveTabIdChange, store]);

  useEffect(
    () => {
      if (defaultActiveTabId) {
        store.setActiveTabId(defaultActiveTabId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <TabsProvider store={store}>
      <div css={rootStyles}>
        <TabsList
          disablePlusButton={disablePlusButton}
          hidePlusButton={hidePlusButton}
          onPlusButtonClick={onPlusButtonClick}
          plusButtonHelperText={plusButtonHelperText}
        />
        <TabsContent />
      </div>
    </TabsProvider>
  );
});

const TabsContent = observer(() => {
  const store = useTabsStore();
  const activeTab = store.activeTab;
  if (!activeTab) {
    return null;
  }
  return <div css={tabContentStyles}>{activeTab.content}</div>;
});

const tabContentStyles = (theme: ExactTheme) => css`
  background-color: ${theme.colors.canvas.subtle};
  border-radius: 0 6px 6px 6px;
  border: 1px solid ${theme.colorsV2.grey[100]};
  outline: none;
  flex-grow: 1;
  height: calc(100% - 38px);
  overflow: hidden;
`;

const rootStyles = css`
  height: 100%;
  display: flex;
  flex-direction: column;
`;
